import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import PageLayout from '../components/ui/PageLayout';
import StepperHeader from '../components/ui/StepperHeader';
import { retreatFlowSteps } from '../domain/retreatFlowSteps';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';
import { buildRegistrationSummary, generateConfirmationId } from '../domain/registrationLogic';

export default function SubmitPage() {
  const { state, dispatch } = useRegistration();
  const navigate = useNavigate();
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);

  const summary = useMemo(() => buildRegistrationSummary(state), [state]);

  if (!state.selectedRetreatId) {
    return <Navigate to="/retreat" replace />;
  }

  if (state.selectedStudentIds.length === 0) {
    return <Navigate to="/students" replace />;
  }

  const onSubmit = () => {
    const confirmationId = generateConfirmationId();

    dispatch({
      type: registrationActionTypes.SUBMIT_SUCCESS,
      payload: {
        confirmationId,
        submittedAt: new Date().toISOString()
      }
    });

    navigate('/confirmation');
  };

  return (
    <PageLayout>
      <StepperHeader
        title="Checkout"
        subtitle="Confirm and submit"
        steps={retreatFlowSteps}
        activeIndex={4}
        onBack={() => navigate('/review')}
      />

      <div className="yk-item">
        <div><strong>Amount Due</strong></div>
        <div className="yk-item__meta">$0</div>
        <div className="yk-item__meta">Payment: Not collected in Phase 1</div>
      </div>

      <div className="yk-item">
        <div><strong>Agreement</strong></div>
        <div className="flex items-start justify-between gap-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={agreementAccepted}
              onChange={(e) => setAgreementAccepted(e.target.checked)}
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">
                I have reviewed all details and agree to the terms & conditions
              </div>
              <div className="text-xs text-gray-500">Required to submit the registration.</div>
            </div>
          </label>

          <button
            type="button"
            onClick={() => setIsAgreementOpen(true)}
            className="shrink-0 text-xs font-semibold text-gray-700 hover:text-gray-900 underline"
          >
            View Agreement
          </button>
        </div>
      </div>

      <div className="yk-item">
        <div><strong>Submission Payload (Mock)</strong></div>
        <pre className="yk-pre">{JSON.stringify(summary, null, 2)}</pre>
      </div>

      <Button type="button" variant="primary" onClick={onSubmit} disabled={!agreementAccepted}>
        Submit FREE Registration
      </Button>

      {isAgreementOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsAgreementOpen(false);
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-white/60 overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gradient-to-br from-white via-slate-50 to-violet-50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Terms & Conditions</div>
                <div className="text-xs text-gray-500">Please read before proceeding.</div>
              </div>
              <button
                type="button"
                onClick={() => setIsAgreementOpen(false)}
                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-xs" />
              </button>
            </div>

            <div className="p-5">
              <div className="rounded-2xl bg-white border border-gray-200 px-4 py-4">
                <div className="text-xs text-gray-700">
                  Agreement text placeholder. Replace with the official retreat registration agreement.
                </div>
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAgreementOpen(false)}
                  className="rounded-full bg-white hover:bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-200 shadow-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAgreementAccepted(true);
                    setIsAgreementOpen(false);
                  }}
                  className="rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-md"
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
